import React from 'react';
import { isAxiosError } from 'axios';
import { Formik } from 'formik';
import { number, object, string } from 'yup';
import { Box, Typography } from '@mui/material';
import { CalendarDateService } from '@uplift/services';
import { SupervisionMeetingMethod } from '@uplift/api';

import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useCreateSupervisionMeetingMutation, useProviderSuperviseeQuery } from '@uplift/queries';
import { DateTimePicker, FormModal, Loader, ModalActions, ModalContent, ModalTitle, NumberInput, RadioControl, SubmitButton, TextInput, useAlert } from '@uplift/ui-v2';
import { NoteSection } from '../../../notes-page/components/NoteSection';


const METHOD_VALUES: SupervisionMeetingMethod[] = [ 'video', 'phone', 'in_person' ];

type FormValues = {
  meetingDate: string;
  duration: number | '';
  method: SupervisionMeetingMethod | '';
  summary: string;
};

const validationSchema = object({
  meetingDate: string()
    .required('Date is required')
    .test(
      'not-future',
      'Date/time cannot be in the future',
      value => !value || CalendarDateService.date(value).valueOf() <= CalendarDateService.date().valueOf()
    ),
  duration: number()
    .min(1, 'Duration must be at least 1 minute')
    .required('Duration is required'),
  method: string().oneOf(METHOD_VALUES).required('Select a method'),
  summary: string().required('Summary is required'),
});


export type SupervisionMeetingFormModalProps = {
  open: boolean;
  onClose: () => void;
  collaborativeMatchId: string;
};


const SuperviseeDetails: React.VFC<{ collaborativeMatchId: string }> = ({ collaborativeMatchId }) => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const { data: superviseeMatch } = useProviderSuperviseeQuery(me.id, collaborativeMatchId);
  const prescriberLabel = superviseeMatch.prescriberType ? l10n.resources.provider.prescriberTypes[superviseeMatch.prescriberType] : '';
  const superviseeDisplay = `${superviseeMatch.user.firstName} ${superviseeMatch.user.lastName}${prescriberLabel ? `, ${prescriberLabel}` : ''}`;

  return (
    <NoteSection title={superviseeDisplay} />
  );
};


export const SupervisionMeetingFormModal: React.VFC<SupervisionMeetingFormModalProps> = ({ open, onClose, collaborativeMatchId }) => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const timezone = useTimezone();
  const alert = useAlert();
  const createMeetingMutation = useCreateSupervisionMeetingMutation();

  const initialValues: FormValues = {
    meetingDate: '',
    duration: '',
    method: '',
    summary: '',
  };
  const handleSubmit = async (values: FormValues) => {
    const meetingDate = CalendarDateService.date(values.meetingDate).toISOString();

    const payload = {
      providerId: me.id,
      matchId: collaborativeMatchId,
      meetingDate,
      durationMinutes: Number(values.duration),
      method: values.method as SupervisionMeetingMethod,
      summary: values.summary,
    };


    try {
      await createMeetingMutation.mutateAsync(payload);

      alert.success('Supervision meeting submitted');
      onClose();
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.error) {
        alert.error(err.response.data.error);
        return;
      }

      alert.error('Unable to submit supervision meeting');
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      <FormModal open={open} maxWidth="md" fullWidth onClose={onClose}>
        <ModalTitle onClose={onClose}>Supervision Meeting</ModalTitle>
        <ModalContent>
          <Box display="flex" flexDirection="column" gap="24px">
            <React.Suspense fallback={<Loader height="80px" />}>
              <SuperviseeDetails collaborativeMatchId={collaborativeMatchId} />
            </React.Suspense>

            <NoteSection title="Details">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, pb: 3 }}>
                <DateTimePicker
                  name="meetingDate"
                  label="Date and Time of Meeting"
                  format="MM/DD/YYYY hh:mm A"
                  valueFormat="YYYY-MM-DDTHH:mm:ssZ"
                  timezone={timezone}
                  disableFuture
                  style={{ width: 250 }}
                />
                <NumberInput
                  name="duration"
                  label="Duration (minutes)"
                  numberInputProps={{ decimalScale: 0, allowNegative: false, min: 1 }}
                  sx={{ width: 225 }}
                />
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Method of Communication</Typography>
                  <RadioControl
                    name="method"
                    values={METHOD_VALUES.map(value => ({
                      value,
                      label: l10n.supervisees.meetingMethods[value],
                    }))}
                  />
                </Box>
              </Box>
            </NoteSection>

            <NoteSection title="Summary">
              <Box sx={{ px: 3, pb: 3 }}>
                <TextInput
                  name="summary"
                  label="Meeting Summary"
                  multiline
                  fullWidth
                  rows={4}
                  placeholder="Summarize the discussion, including patient treatment and care, needs assessment, and opportunities to improve patient care. Note any follow-up items."
                />
              </Box>
            </NoteSection>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Signing and submitting serves as your attestation of this supervision meeting record.
          </Typography>
        </ModalContent>

        <ModalActions>
          <SubmitButton color="secondary">Sign and Submit</SubmitButton>
        </ModalActions>
      </FormModal>
    </Formik>
  );
};
